import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment } from 'react';

import { useTranslate } from '~/shared/context/use-translate';

import { InviteTeamMemberDialog } from '../../company-settings/tabs/organization-tab/users-and-roles/InviteTeamMemberDialog';
import { JOB_ARCHITECTURE_INVITATION_ROLES, useInviteTeamMember } from '../api/use-invite-team-member';
import { type JobItemDetails, useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { AuditTable, AuditTableSkeleton } from './AuditTable';

const AuditTableMetadataContent: FunctionComponent<{
    readonly architectureId: string;
    readonly isArchived?: boolean;
    readonly item: JobItemDetails;
    readonly version: string | undefined;
}> = ({ architectureId, isArchived, item, version }) => {
    const translate = useTranslate();
    const { availableMethods, closeInviteDialog, handleInvite, isInviteDialogOpen, openInviteDialog } =
        useInviteTeamMember({
            architectureId: item.architectureId,
            itemId: item.id,
            itemType: item.type,
        });
    const {
        data: versions,
        error,
        isLoading: isLoadingVersions,
    } = useJobItemVersions({
        architectureId: item.architectureId,
        itemId: item.id,
        itemType: item.type,
    });

    const currentVersion = versions?.find((v) => v.version === Number(version)) ?? versions?.[0];

    if (isLoadingVersions || error !== null || is.nullOrUndefined(currentVersion)) {
        return null;
    }

    return (
        <Fragment>
            <AuditTable
                architectureId={architectureId}
                currentVersion={currentVersion}
                isCollapsible={true}
                isArchived={isArchived}
                item={item}
                onInviteReviewer={openInviteDialog}
            />
            <InviteTeamMemberDialog
                allowedRoles={JOB_ARCHITECTURE_INVITATION_ROLES}
                availableMethods={availableMethods}
                open={isInviteDialogOpen}
                title={translate('pages.company-settings.users-and-roles.invite-dialog.assign-review-title')}
                onClose={closeInviteDialog}
                onInvite={handleInvite}
            />
        </Fragment>
    );
};

export const SidepanelMetadata: FunctionComponent<{
    readonly architectureId: string;
    readonly isArchived?: boolean;
    readonly itemId: string;
    readonly itemType?: 'job-level' | 'job-role';
    readonly version: string | undefined;
}> = ({ architectureId, isArchived, itemId, itemType = 'job-role', version }) => {
    const { data: item, error, isLoading } = useJobItemDetails({ architectureId, itemId, itemType, version });
    const translate = useTranslate();

    if (isLoading) {
        return <AuditTableSkeleton />;
    }
    if (!is.nullOrUndefined(error)) {
        return (
            <div className="text-sm text-gray-700">
                {translate('pages.job-architecture-v2.job-profile-error-prefix')}
            </div>
        );
    }
    if (is.nullOrUndefined(item)) {
        return (
            <div className="text-sm text-gray-700">
                {translate('pages.job-architecture-v2.job-profile-no-data-available', { itemType })}
            </div>
        );
    }

    return (
        <AuditTableMetadataContent
            architectureId={architectureId}
            isArchived={isArchived}
            item={item}
            version={version}
        />
    );
};
